/**
 * Job sheets — the Company's named job sheet templates (#188), rendered
 * through the shared roster and studio shell (#241). This file is the job
 * sheet's half: its roster copy, its four API calls, its one section map,
 * and the two document-wide company values that edit as fields beside the
 * sheet.
 */
import { useState } from "react";
import forms from "../../components/ui/field.module.css";
import type {
  CompanySettings,
  CompanySettingsUpdate,
} from "../../lib/company-settings-api";
import {
  documentPreviewRequest,
  getDocumentPreview,
} from "../../lib/document-preview-api";
import {
  createJobSheetTemplate,
  deleteJobSheetTemplate,
  fullDetailSections,
  listJobSheetTemplates,
  updateJobSheetTemplate,
  type JobSheetTemplate,
  type JobsheetSections,
} from "../../lib/job-sheet-templates-api";
import { previewKeyOf, usePreview } from "./use-preview";
import { FieldLabel } from "./section-chrome";
import {
  sectionCount,
  TemplateRoster,
  type RosterArtifact,
} from "./TemplateRoster";
import {
  httpUrlOr,
  StudioShell,
  useSettingFields,
  useTemplateStudio,
} from "./studio-shell";

const JOB_SHEET_ROSTER: RosterArtifact<JobSheetTemplate> = {
  idPrefix: "job-sheet",
  title: "Job sheets",
  description:
    "The documents Contractors get with their Assignments. Each template picks which sections its sheet includes.",
  defaultDescription:
    "New Assignments use this template unless a Contractor or Assignment overrides it.",
  floorLabel: "Full job sheet",
  emptyText: "No templates yet. Contractors receive the full job sheet.",
  deleteWarning:
    "Contractors and Assignments using it must be moved to another template first.",
  countLine: (t) => `${sectionCount(t.sections)} sections`,
  list: listJobSheetTemplates,
  create: (name) =>
    createJobSheetTemplate({ name, sections: fullDetailSections() }),
  remove: deleteJobSheetTemplate,
  setDefault: (t, isDefault) =>
    updateJobSheetTemplate(t.id, {
      name: t.name,
      sections: t.sections,
      is_default: isDefault,
    }),
};

export function JobSheetsSection({
  settings,
  onPatch,
  onBack,
}: {
  settings: CompanySettings;
  onPatch: (patch: CompanySettingsUpdate) => Promise<void>;
  /** Returns to the Documents & emails gallery from the templates list. */
  onBack?: () => void;
}) {
  return (
    <TemplateRoster
      artifact={JOB_SHEET_ROSTER}
      onBack={onBack}
      renderStudio={(studio) => (
        <JobSheetStudio
          key={studio.template.id}
          settings={settings}
          onPatch={onPatch}
          {...studio}
        />
      )}
    />
  );
}

/* ─── Template editor ───────────────────────────────────────────────────── */

/**
 * The job sheet's studio: the sheet IS the controls (see StudioShell), with
 * the document-wide company values as labelled fields beside it — an inline
 * contenteditable for a line woven into the document would be undiscoverable
 * (DESIGN.md "Settings studios").
 */
function JobSheetStudio({
  template,
  settings,
  onPatch,
  onSaved,
  onBack,
}: {
  template: JobSheetTemplate;
  settings: CompanySettings;
  onPatch: (patch: CompanySettingsUpdate) => Promise<void>;
  onSaved: (next: JobSheetTemplate) => void;
  onBack: () => void;
}) {
  const [extraNotes, setExtraNotes] = useState(settings.extra_notes_text);
  const [contractorTermsUrl, setContractorTermsUrl] = useState(
    settings.contractor_terms_url,
  );
  const { commit, fieldError } =
    useSettingFields<CompanySettingsUpdate>(onPatch);

  const studio = useTemplateStudio<JobSheetTemplate, JobsheetSections>({
    template,
    initialSections: template.sections,
    activeSections: (draft) => draft,
    toggleSection: (draft, key, next) => ({ ...draft, [key]: next }),
    save: (name, sections) =>
      updateJobSheetTemplate(template.id, { name, sections }),
    onSaved,
    docClass: "jobsheet-doc",
  });

  // A half-typed terms URL previews as the saved one (the server rejects
  // malformed URLs); the note field previews live as typed.
  const request = documentPreviewRequest(settings, "jobsheet", {
    format: "html",
    extra_notes_text: extraNotes,
    contractor_terms_url: httpUrlOr(
      contractorTermsUrl,
      settings.contractor_terms_url,
    ),
    jobsheet_sections: studio.draft,
    jobsheet_template_name: template.name,
  });

  const {
    result,
    loading,
    error: previewError,
  } = usePreview(previewKeyOf(request), (signal) =>
    getDocumentPreview({ ...request, signal }),
  );

  return (
    <StudioShell
      backLabel="All job sheet templates"
      onBack={onBack}
      savedName={template.name}
      name={studio.name}
      description="The job sheet Contractors on this template receive."
      saveError={studio.saveError}
      previewError={previewError}
      fieldsNote="These values apply to every job sheet, whichever template."
      fields={
        <>
          <div>
            <FieldLabel
              htmlFor="jobsheet-extra-notes"
              tooltip="A standing note shown on every job sheet (with Access & on-site notes) and every quote."
            >
              Extra notes
            </FieldLabel>
            <textarea
              id="jobsheet-extra-notes"
              rows={3}
              className={forms.input}
              value={extraNotes}
              onChange={(e) => setExtraNotes(e.target.value)}
              onBlur={() =>
                void commit(
                  "extraNotes",
                  "extra_notes_text",
                  extraNotes,
                  settings.extra_notes_text,
                )
              }
            />
            {fieldError("extraNotes")}
          </div>
          <div>
            <FieldLabel
              htmlFor="jobsheet-contractor-terms-url"
              tooltip="Linked at the bottom of every job sheet so crew can read your contractor terms."
            >
              Your contractor terms web page
            </FieldLabel>
            <input
              id="jobsheet-contractor-terms-url"
              type="url"
              className={forms.input}
              value={contractorTermsUrl}
              onChange={(e) => setContractorTermsUrl(e.target.value)}
              onBlur={() =>
                void commit(
                  "contractorTermsUrl",
                  "contractor_terms_url",
                  contractorTermsUrl,
                  settings.contractor_terms_url,
                )
              }
            />
            {fieldError("contractorTermsUrl")}
          </div>
        </>
      }
      hiddenCSS={studio.hiddenCSS}
      result={result}
      loading={loading}
      onSheetClick={studio.handleSheetClick}
    />
  );
}
